import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface HierarchySearchResultsProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: frame 33's "Kết quả tìm" state — the tree collapses to the matching
 * rows, each carrying whatever ancestor context is available, dimmed.
 *
 * WHY THIS IS A FLAT LIST rather than a pruned tree. Search has to be
 * server-side: the tree only holds levels the user has expanded, so a match
 * three levels down an unopened branch does not exist on the client to filter.
 * `GET /organizations?search=` answers it, but returns flat rows whose only
 * hierarchy information is `parent` (a bare uuid) — there is no ancestor-path
 * field and no endpoint to resolve a chain in one call. So each result names its
 * parent when that parent is already loaded (`knownOrgs`, populated by the
 * expansions so far and by the result set itself) and says "cấp cha chưa tải"
 * when it is not, rather than pretending to know the full path.
 *
 * Selecting a result selects it in the detail panel; it does not auto-expand the
 * tree to it, because walking down to an arbitrary node means one request per
 * level with no bulk path endpoint to shortcut it.
 */
export function HierarchySearchResults({ vm }: HierarchySearchResultsProps) {
    const { t } = useTranslation();
    const {
        searchQuery,
        searchResults,
        searchResultsQuery,
        searchTruncated,
        knownOrgs,
        unitTypesById,
        selectedOrgId,
        setSelectedOrgId,
    } = vm;

    if (searchResultsQuery.isLoading) {
        return (
            <div className="hz-tree-skeleton" role="status" aria-live="polite">
                <span className="portal-sr-only">
                    {t('console.hierarchy.searchResults.loadingSrLabel')}
                </span>
                {[0, 1, 2].map((i) => (
                    <span key={i} className="hz-skeleton-row">
                        <Skeleton height="24px" width="24px" />
                        <Skeleton height="0.75rem" width={`${50 - i * 7}%`} />
                    </span>
                ))}
            </div>
        );
    }

    if (searchResultsQuery.isError) {
        return (
            <ErrorState
                description={t('console.hierarchy.searchResults.errorDescription')}
                onRetry={() => void searchResultsQuery.refetch()}
                retrying={searchResultsQuery.isFetching}
            />
        );
    }

    if (searchResults.length === 0) {
        return (
            <EmptyState
                title={t('console.hierarchy.searchResults.noMatchTitle', {
                    query: searchQuery.trim(),
                })}
            />
        );
    }

    return (
        <div className="hz-search-results">
            <p className="hz-search-count">
                {t('console.hierarchy.searchResults.countLabel', {
                    count: searchResults.length,
                    query: searchQuery.trim(),
                })}
            </p>
            {searchResults.map((org) => {
                const parent = org.parent ? (knownOrgs.get(org.parent) ?? null) : null;
                let context: string;
                if (org.parent === null) {
                    context = t('console.hierarchy.searchResults.rootContext');
                } else if (parent) {
                    context = t('console.hierarchy.searchResults.parentContext', {
                        name: parent.name,
                    });
                } else {
                    context = t('console.hierarchy.searchResults.parentNotLoaded');
                }
                /**
                 * EAA-1838: same badge as the tree row, with one extra caveat that is
                 * specific to search — `GET /organizations?search=` spans every org
                 * the CALLER can see, not just the acting org's subtree, so a result
                 * from another root tree carries a type from that root's OWN vocabulary,
                 * which `unitTypesById` (this root's) will not contain. That
                 * resolves to no chip, which is the honest outcome: better silent
                 * than a chip naming a type that means something else here.
                 */
                const unitType = org.unit_type ? unitTypesById.get(org.unit_type) : undefined;
                return (
                    <button
                        key={org.id}
                        type="button"
                        className={
                            selectedOrgId === org.id
                                ? 'hz-search-row hz-search-row--selected'
                                : 'hz-search-row'
                        }
                        onClick={() => setSelectedOrgId(org.id)}
                    >
                        <span className="hz-avatar" data-muted={!org.is_active || undefined}>
                            {org.code.slice(0, 2).toUpperCase()}
                        </span>
                        <span className="hz-search-row-main">
                            <HighlightedName name={org.name} query={searchQuery.trim()} />
                            <span className="hz-search-row-context">{context}</span>
                        </span>
                        {unitType && (
                            <span
                                className={
                                    unitType.is_active
                                        ? 'hz-unit-type-tag'
                                        : 'hz-unit-type-tag hz-unit-type-tag--hidden'
                                }
                            >
                                {unitType.name}
                            </span>
                        )}
                        {!org.is_active && (
                            <span className="hz-moving-tag">
                                {t('console.hierarchy.searchResults.inactiveTag')}
                            </span>
                        )}
                        <code className="hz-code">{org.code}</code>
                    </button>
                );
            })}
            {searchTruncated && (
                <p className="hz-card-note">
                    {t('console.hierarchy.searchResults.truncatedNote')}
                </p>
            )}
        </div>
    );
}

/** Frame 33 highlights the matched substring inside the name. */
function HighlightedName({ name, query }: { name: string; query: string }) {
    const index = query ? name.toLowerCase().indexOf(query.toLowerCase()) : -1;
    if (index < 0) return <span className="hz-name">{name}</span>;
    return (
        <span className="hz-name">
            {name.slice(0, index)}
            <mark className="hz-search-mark">{name.slice(index, index + query.length)}</mark>
            {name.slice(index + query.length)}
        </span>
    );
}

export default HierarchySearchResults;
